import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';

interface Notification {
  id: string;
  type: 'campaign_completed' | 'campaign_failed' | 'limit_warning' | 'limit_reached' | 'payment_success' | 'payment_failed' | 'backup_complete' | 'update_available' | 'trial_expiring' | 'system';
  title: string;
  message: string;
  read: boolean;
  timestamp: string;
  link?: string;
}

const NOTIFICATION_CONFIG: Record<string, { icon: string; bg: string; color: string }> = {
  campaign_completed: { icon: 'ri-check-double-line', bg: 'bg-emerald-50 dark:bg-emerald-500/10', color: 'text-emerald-600 dark:text-emerald-400' },
  campaign_failed: { icon: 'ri-error-warning-line', bg: 'bg-red-50 dark:bg-red-500/10', color: 'text-red-500 dark:text-red-400' },
  limit_warning: { icon: 'ri-alert-line', bg: 'bg-amber-50 dark:bg-amber-500/10', color: 'text-amber-600 dark:text-amber-400' },
  limit_reached: { icon: 'ri-close-circle-line', bg: 'bg-red-50 dark:bg-red-500/10', color: 'text-red-500 dark:text-red-400' },
  payment_success: { icon: 'ri-bank-card-line', bg: 'bg-emerald-50 dark:bg-emerald-500/10', color: 'text-emerald-600 dark:text-emerald-400' },
  payment_failed: { icon: 'ri-bank-card-line', bg: 'bg-red-50 dark:bg-red-500/10', color: 'text-red-500 dark:text-red-400' },
  backup_complete: { icon: 'ri-archive-line', bg: 'bg-primary-50 dark:bg-primary-500/10', color: 'text-primary-600 dark:text-primary-400' },
  update_available: { icon: 'ri-download-cloud-2-line', bg: 'bg-accent-50 dark:bg-accent-500/10', color: 'text-accent-600 dark:text-accent-400' },
  trial_expiring: { icon: 'ri-alarm-warning-line', bg: 'bg-amber-50 dark:bg-amber-500/10', color: 'text-amber-600 dark:text-amber-400' },
  system: { icon: 'ri-information-line', bg: 'bg-secondary-50 dark:bg-secondary-500/10', color: 'text-secondary-600 dark:text-secondary-400' },
};

const mockNotifications: Notification[] = [
  {
    id: 'n1',
    type: 'campaign_completed',
    title: 'Campanha Concluída',
    message: 'Campanha "Promoção de Agosto" foi concluída com sucesso. 1.247 mensagens enviadas, taxa de entrega: 98.4%.',
    read: false,
    timestamp: '2026-08-04T15:30:00Z',
    link: '/campaigns',
  },
  {
    id: 'n2',
    type: 'limit_warning',
    title: 'Limite de Mensagens: 80%',
    message: 'Você já utilizou 8.000 de 10.000 mensagens do seu plano este mês. Considere comprar créditos extras.',
    read: false,
    timestamp: '2026-08-04T14:10:00Z',
    link: '/my-usage',
  },
  {
    id: 'n3',
    type: 'payment_success',
    title: 'Pagamento Aprovado',
    message: 'Seu pagamento de R$ 297,00 (Plano Professional) foi aprovado. Obrigado!',
    read: true,
    timestamp: '2026-08-03T09:00:00Z',
    link: '/invoices',
  },
  {
    id: 'n4',
    type: 'campaign_failed',
    title: 'Falha na Campanha',
    message: 'Campanha "Lembrete de Consulta" foi pausada por falhas consecutivas. Verifique a conexão WhatsApp.',
    read: false,
    timestamp: '2026-08-03T18:45:00Z',
    link: '/campaigns',
  },
  {
    id: 'n5',
    type: 'backup_complete',
    title: 'Backup Concluído',
    message: 'Backup automático realizado com sucesso. Tamanho: 2.4 GB. Destino: Backblaze B2.',
    read: true,
    timestamp: '2026-08-03T03:00:00Z',
    link: '/settings/backup',
  },
  {
    id: 'n6',
    type: 'update_available',
    title: 'Atualização Disponível',
    message: 'Evolution API v2.3.0 está disponível. Esta versão inclui correções de segurança importantes.',
    read: false,
    timestamp: '2026-08-02T11:20:00Z',
    link: '/settings/updates',
  },
  {
    id: 'n7',
    type: 'trial_expiring',
    title: 'Trial Expirando',
    message: 'Seu período de teste termina em 2 dias. Assine um plano para manter o acesso a todos os recursos.',
    read: false,
    timestamp: '2026-08-02T08:00:00Z',
    link: '/subscription',
  },
  {
    id: 'n8',
    type: 'limit_reached',
    title: 'Limite de WhatsApps Atingido',
    message: 'Você atingiu o limite de 3 WhatsApps conectados do seu plano. Faça upgrade para adicionar mais.',
    read: true,
    timestamp: '2026-08-01T16:30:00Z',
    link: '/subscription',
  },
  {
    id: 'n9',
    type: 'system',
    title: 'Manutenção Programada',
    message: 'Manutenção do servidor agendada para 06/08/2026 às 03:00 UTC. Possível instabilidade por até 15 minutos.',
    read: false,
    timestamp: '2026-08-01T12:00:00Z',
  },
  {
    id: 'n10',
    type: 'campaign_completed',
    title: 'Campanha Concluída',
    message: 'Campanha "Boas-vindas Novos Clientes" finalizada. 89 mensagens enviadas com 100% de entrega.',
    read: true,
    timestamp: '2026-07-31T20:15:00Z',
    link: '/campaigns',
  },
  {
    id: 'n11',
    type: 'payment_failed',
    title: 'Pagamento Recusado',
    message: 'Sua tentativa de compra de créditos (R$ 60,00) foi recusada. Verifique os dados do cartão.',
    read: true,
    timestamp: '2026-07-30T14:00:00Z',
    link: '/invoices',
  },
  {
    id: 'n12',
    type: 'system',
    title: 'Webhook Desativado',
    message: 'O webhook "CRM — Novo Contato" foi desativado automaticamente após 5 falhas consecutivas.',
    read: true,
    timestamp: '2026-07-29T09:30:00Z',
    link: '/integrations',
  },
];

export default function NotificationsPage() {
  const { profile } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // Filter notifications relevant to current user
  const userNotifications = notifications.filter((n) => {
    // System-wide notifications show for everyone
    if (n.type === 'system' || n.type === 'update_available') return true;
    // Admin users see backup and system notifications
    if (profile?.role === 'admin' && (n.type === 'backup_complete' || n.type === 'limit_reached')) return true;
    // All other notifications are user-specific - show only to trial/paid users
    return true;
  });

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const deleteNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const filtered = filter === 'unread'
    ? userNotifications.filter((n) => !n.read)
    : userNotifications;

  const unreadCount = userNotifications.filter((n) => !n.read).length;

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return 'Agora mesmo';
    if (diffMin < 60) return `Há ${diffMin} min`;
    if (diffHrs < 24) return `Há ${diffHrs}h`;
    if (diffDays < 7) return `Há ${diffDays} dias`;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Notificações</h2>
          <p className="text-sm text-foreground-500 mt-1">
            {unreadCount > 0
              ? `${unreadCount} notificação${unreadCount !== 1 ? 'ões' : ''} não lida${unreadCount !== 1 ? 's' : ''}`
              : 'Todas as notificações foram lidas'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllAsRead}
              className="px-3 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-500/10 rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            >
              <i className="ri-check-double-line" /> Marcar todas como lidas
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
            filter === 'all'
              ? 'bg-primary-500 text-white'
              : 'bg-background-100 dark:bg-background-200 text-foreground-600 hover:bg-background-200 dark:hover:bg-background-300'
          }`}
        >
          Todas ({notifications.length})
        </button>
        <button
          type="button"
          onClick={() => setFilter('unread')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5 ${
            filter === 'unread'
              ? 'bg-primary-500 text-white'
              : 'bg-background-100 dark:bg-background-200 text-foreground-600 hover:bg-background-200 dark:hover:bg-background-300'
          }`}
        >
          Não lidas
          {unreadCount > 0 && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              filter === 'unread' ? 'bg-white/20 text-white' : 'bg-primary-500 text-white'
            }`}>
              {unreadCount}
            </span>
          )}
        </button>
      </div>

      {/* Notifications List */}
      <div className="space-y-2">
        {filtered.length === 0 ? (
          <div className="bg-white dark:bg-background-100 rounded-2xl border border-background-200 dark:border-background-300 p-12 text-center">
            <div className="w-14 h-14 rounded-full bg-background-100 dark:bg-background-200 flex items-center justify-center mx-auto mb-4">
              <i className="ri-notification-off-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700 dark:text-foreground-300">
              {filter === 'unread' ? 'Nenhuma notificação não lida' : 'Nenhuma notificação'}
            </p>
            <p className="text-xs text-foreground-500 mt-1">
              {filter === 'unread' ? 'Você já leu todas as notificações!' : 'As notificações aparecerão aqui'}
            </p>
          </div>
        ) : (
          filtered.map((notif) => {
            const config = NOTIFICATION_CONFIG[notif.type];
            return (
              <div
                key={notif.id}
                className={`bg-white dark:bg-background-100 rounded-2xl border p-4 transition-all hover:border-background-300 dark:hover:border-background-400 group ${
                  !notif.read
                    ? 'border-primary-200 dark:border-primary-500/20 bg-primary-50/30 dark:bg-primary-500/5'
                    : 'border-background-200 dark:border-background-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bg}`}>
                    <i className={`${config.icon} text-lg ${config.color}`} />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <h4 className="text-sm font-semibold text-foreground-900 dark:text-foreground-100">
                        {notif.title}
                      </h4>
                      {!notif.read && (
                        <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0 animate-pulse" />
                      )}
                    </div>
                    <p className="text-sm text-foreground-600 dark:text-foreground-400 leading-relaxed">
                      {notif.message}
                    </p>
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-xs text-foreground-400">{formatDate(notif.timestamp)}</span>
                      {notif.link && (
                        <Link
                          to={notif.link}
                          className="text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                        >
                          Ver detalhes <i className="ri-arrow-right-line text-[10px]" />
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    {!notif.read && (
                      <button
                        type="button"
                        onClick={() => markAsRead(notif.id)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                        title="Marcar como lida"
                      >
                        <i className="ri-check-line text-sm" />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => deleteNotification(notif.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
                      title="Excluir"
                    >
                      <i className="ri-close-line text-sm" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Clear all */}
      {notifications.length > 0 && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setNotifications([])}
            className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
          >
            Limpar todas as notificações
          </button>
        </div>
      )}
    </div>
  );
}